import { Injectable, OnDestroy, inject } from '@angular/core';
import { ThemeService } from './theme';
import { environment } from '../../environments/environment';

declare global {
  interface Window {
    IOMm?: (...args: unknown[]) => void;
    initInfonlineLoader?: () => void;
  }
}

/** Dispatched by index.html once the (asynchronously loaded) IOMm queue actually exists. */
export const INFONLINE_READY_EVENT = 'infonline:ready';

/**
 * IVW/INFOnline Measurement Manager pageview tracking (AI-577) — production builds only, and
 * Finnet/Portal only, never Zero. `environment.production` is the sole gate for the whole
 * mechanism: nothing (not even a loader-script fetch) happens on a dev/test build (see
 * `index.html` — it no longer starts the loader itself; only this service does, and only when
 * this check passes). Core fires the initial impression and every pseudo-navigation pageview
 * from its lifecycle. Both checks are re-verified here as defense in depth, and to keep them
 * independently testable at this call site.
 *
 * `window.IOMm` only exists once index.html's loader `<script>` has loaded — Angular can
 * already be interactive before that. A pageview requested in that gap is queued and flushed
 * once `INFONLINE_READY_EVENT` fires, instead of silently dropped by optional chaining.
 *
 * A single `cp: 'ai-analyse'` is used everywhere (per-section codes would need registering
 * with the User Team in the IVW backend first).
 */
@Injectable({ providedIn: 'root' })
export class InfonlineService implements OnDestroy {
  private readonly themeService = inject(ThemeService);
  private ready = typeof window !== 'undefined' && typeof window.IOMm === 'function';
  private readonly pending: string[] = [];
  // Bound so ngOnDestroy can remove this exact listener — otherwise each test's fresh
  // instance leaks one onto the shared `window`.
  private readonly onReady = () => this.flush();

  constructor() {
    // Not worth registering at all on a dev/test build — pageview() never gets far enough to
    // queue anything there, so this listener would never have anything to flush.
    if (environment.production && !this.ready && typeof window !== 'undefined') {
      window.addEventListener(INFONLINE_READY_EVENT, this.onReady, { once: true });
    }
  }

  ngOnDestroy(): void {
    if (typeof window !== 'undefined') {
      window.removeEventListener(INFONLINE_READY_EVENT, this.onReady);
    }
  }

  pageview(cp = 'ai-analyse'): void {
    if (!environment.production || !this.themeService.isFinnet()) return;

    // Starts the loader if nothing has yet (index.html itself never does) — also recovers a
    // session that starts on Zero and only becomes Finnet later at runtime. No-op if the
    // loader is already running.
    window.initInfonlineLoader?.();

    if (this.ready || typeof window.IOMm === 'function') {
      this.ready = true;
      window.IOMm?.('pageview', { cp });
    } else {
      this.pending.push(cp);
    }
  }

  private flush(): void {
    this.ready = true;
    const pending = this.pending.splice(0);
    // Drain unconditionally so the queue never grows stale entries, but re-check isFinnet()
    // and the route — both the theme and (via routerLink) the route can change at runtime
    // between a pageview being queued and the loader becoming ready, and a view queued while
    // eligible must not go out once either condition no longer holds. environment.production
    // isn't re-checked here: unlike those two, it can't change at runtime, and pageview()
    // already keeps anything from reaching `pending` at all when it's false.
    const path = location.pathname.replace(/\/+$/, '') || '/';
    const isStandaloneRoute = /^\/(?:eps|privacy|terms)(?:;[^/]*)?$/.test(path);
    if (!this.themeService.isFinnet() || isStandaloneRoute) return;
    pending.forEach((cp) => window.IOMm?.('pageview', { cp }));
  }
}
